import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useGym } from '../contexts/GymContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Building2, 
  CreditCard,
  Calendar,
  CheckCircle2,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  DollarSign,
  TrendingDown,
  Wallet,
  Target,
  Info
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { formatCurrency, formatNumber, formatPercent } from '../utils/formatters';

const PRIMARY_COLOR = '#22C55E';
const SECONDARY_COLOR = '#10B981';
const DANGER_COLOR = '#EF4444';

export const CompromissosPage = () => {
  const { selectedGym } = useGym();
  const [compromissos, setCompromissos] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCompromisso, setEditingCompromisso] = useState(null);
  const [expandedCompromisso, setExpandedCompromisso] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    tipo: 'investimento',
    valor_total: '',
    valor_parcela: '',
    total_parcelas: '',
    parcelas_ja_pagas: '',  // Quantas parcelas já foram pagas
    data_inicio_mes: 'janeiro',
    data_inicio_ano: new Date().getFullYear().toString(),
    data_fim: '',
    taxa_juros_mensal: '',
    descricao: '',
    categoria: ''
  });

  const meses = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
  ];
  
  const anosDisponiveis = Array.from({ length: 10 }, (_, i) => (new Date().getFullYear() - 2 + i).toString());

  const loadData = useCallback(async () => {
    if (!selectedGym) return;
    
    try {
      setLoading(true);
      const [compromissosRes, dashboardRes] = await Promise.all([
        api.get(`/compromissos?gym_id=${selectedGym.id}`),
        api.get(`/compromissos-dashboard?gym_id=${selectedGym.id}`)
      ]);
      
      setCompromissos(compromissosRes.data);
      setDashboard(dashboardRes.data);
    } catch (error) {
      toast.error('Erro ao carregar compromissos');
    } finally {
      setLoading(false);
    }
  }, [selectedGym]);

  useEffect(() => {
    if (selectedGym) {
      loadData();
    }
  }, [selectedGym, loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const dataInicio = `${formData.data_inicio_mes}/${formData.data_inicio_ano}`;
    
    if (!formData.nome || !formData.valor_parcela || !formData.total_parcelas || !formData.data_inicio_mes || !formData.data_inicio_ano) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    
    try {
      const payload = {
        gym_id: selectedGym.id,
        nome: formData.nome,
        tipo: formData.tipo,
        valor_total: parseFloat(formData.valor_total) || (parseFloat(formData.valor_parcela) * parseInt(formData.total_parcelas)),
        valor_parcela: parseFloat(formData.valor_parcela),
        total_parcelas: parseInt(formData.total_parcelas),
        parcelas_ja_pagas: parseInt(formData.parcelas_ja_pagas) || 0,
        data_inicio: dataInicio,
        data_fim: formData.data_fim || calcularDataFim(dataInicio, parseInt(formData.total_parcelas)),
        taxa_juros_mensal: parseFloat(formData.taxa_juros_mensal) || 0,
        descricao: formData.descricao,
        categoria: formData.categoria
      };
      
      if (editingCompromisso) {
        await api.put(`/compromissos/${editingCompromisso.id}`, payload);
        toast.success('Compromisso atualizado!');
      } else {
        await api.post('/compromissos', payload);
        toast.success('Compromisso cadastrado!');
      }
      
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar compromisso');
    }
  };

  const calcularDataFim = (dataInicio, parcelas) => {
    if (!dataInicio || !parcelas) return '';
    
    try {
      let mes, ano;
      if (dataInicio.includes('/')) {
        [mes, ano] = dataInicio.split('/');
        mes = meses.indexOf(mes.toLowerCase());
        ano = parseInt(ano);
      } else {
        [ano, mes] = dataInicio.split('-').map(Number);
        mes = mes - 1;
      }
      
      const mesFim = (mes + parcelas - 1) % 12;
      const anoFim = ano + Math.floor((mes + parcelas - 1) / 12);
      
      return `${meses[mesFim]}/${anoFim}`;
    } catch {
      return '';
    }
  };

  const handleEdit = (compromisso) => {
    setEditingCompromisso(compromisso);
    // Parse data_inicio no formato "mes/ano"
    let mesInicio = 'janeiro';
    let anoInicio = new Date().getFullYear().toString();
    if (compromisso.data_inicio) {
      const parts = compromisso.data_inicio.split('/');
      if (parts.length === 2) {
        mesInicio = parts[0].toLowerCase();
        anoInicio = parts[1];
      }
    }
    setFormData({
      nome: compromisso.nome,
      tipo: compromisso.tipo,
      valor_total: compromisso.valor_total?.toString() || '',
      valor_parcela: compromisso.valor_parcela?.toString() || '',
      total_parcelas: compromisso.total_parcelas?.toString() || '',
      parcelas_ja_pagas: compromisso.parcelas_pagas?.toString() || '0',
      data_inicio_mes: mesInicio,
      data_inicio_ano: anoInicio,
      data_fim: compromisso.data_fim || '',
      taxa_juros_mensal: compromisso.taxa_juros_mensal?.toString() || '',
      descricao: compromisso.descricao || '',
      categoria: compromisso.categoria || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este compromisso e todas as suas parcelas?')) return;
    
    try {
      await api.delete(`/compromissos/${id}`);
      toast.success('Compromisso excluído!');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleMarcarPaga = async (parcelaId) => {
    try {
      await api.post(`/parcelas/${parcelaId}/pagar`);
      toast.success('Parcela marcada como paga!');
      loadData();
      // Recarregar parcelas do compromisso expandido
      if (expandedCompromisso) {
        loadParcelas(expandedCompromisso);
      }
    } catch (error) {
      toast.error('Erro ao marcar parcela');
    }
  };

  const handleDesfazerPagamento = async (parcelaId) => {
    try {
      await api.post(`/parcelas/${parcelaId}/desfazer-pagamento`);
      toast.success('Pagamento desfeito!');
      loadData();
      if (expandedCompromisso) {
        loadParcelas(expandedCompromisso);
      }
    } catch (error) {
      toast.error('Erro ao desfazer pagamento');
    }
  };

  const [parcelasExpanded, setParcelasExpanded] = useState([]);
  
  const loadParcelas = async (compromissoId) => {
    try {
      const response = await api.get(`/compromissos/${compromissoId}/parcelas`);
      setParcelasExpanded(response.data);
    } catch (error) {
    }
  };

  const toggleExpand = (compromissoId) => {
    if (expandedCompromisso === compromissoId) {
      setExpandedCompromisso(null);
      setParcelasExpanded([]);
    } else {
      setExpandedCompromisso(compromissoId);
      loadParcelas(compromissoId);
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      tipo: 'investimento',
      valor_total: '',
      valor_parcela: '',
      total_parcelas: '',
      parcelas_ja_pagas: '',
      data_inicio_mes: 'janeiro',
      data_inicio_ano: new Date().getFullYear().toString(),
      data_fim: '',
      taxa_juros_mensal: '',
      descricao: '',
      categoria: ''
    });
    setEditingCompromisso(null);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: PRIMARY_COLOR }}></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 overflow-x-hidden" data-testid="compromissos-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Investimentos & Financiamentos</h1>
            <p className="text-zinc-400">Gerencie seus compromissos financeiros com parcelas</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button 
                className="gap-2"
                style={{ backgroundColor: PRIMARY_COLOR }}
                data-testid="add-compromisso-btn"
              >
                <Plus className="w-4 h-4" />
                Novo Compromisso
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingCompromisso ? 'Editar Compromisso' : 'Novo Compromisso'}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label className="text-zinc-300">Nome *</Label>
                    <Input
                      value={formData.nome}
                      onChange={(e) => setFormData({...formData, nome: e.target.value})}
                      placeholder="Ex: Financiamento Equipamentos"
                      className="bg-zinc-800 border-zinc-700 text-white"
                      data-testid="input-nome"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-zinc-300">Tipo</Label>
                    <select
                      value={formData.tipo}
                      onChange={(e) => setFormData({...formData, tipo: e.target.value})}
                      className="w-full h-10 px-3 rounded-md bg-zinc-800 border border-zinc-700 text-white"
                      data-testid="select-tipo"
                    >
                      <option value="investimento">Investimento</option>
                      <option value="financiamento">Financiamento</option>
                    </select>
                  </div>
                  
                  <div>
                    <Label className="text-zinc-300">Categoria</Label>
                    <Input
                      value={formData.categoria}
                      onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                      placeholder="Ex: Equipamentos, Obras..."
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-zinc-300">Valor da Parcela *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.valor_parcela}
                      onChange={(e) => setFormData({...formData, valor_parcela: e.target.value})}
                      placeholder="0,00"
                      className="bg-zinc-800 border-zinc-700 text-white"
                      data-testid="input-valor-parcela"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-zinc-300">Total de Parcelas *</Label>
                    <Input
                      type="number"
                      value={formData.total_parcelas}
                      onChange={(e) => setFormData({...formData, total_parcelas: e.target.value})}
                      placeholder="12"
                      className="bg-zinc-800 border-zinc-700 text-white"
                      data-testid="input-total-parcelas"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-zinc-300">Parcelas Já Pagas</Label>
                    <Input
                      type="number"
                      min="0"
                      max={formData.total_parcelas || 999}
                      value={formData.parcelas_ja_pagas}
                      onChange={(e) => setFormData({...formData, parcelas_ja_pagas: e.target.value})}
                      placeholder="0"
                      className="bg-zinc-800 border-zinc-700 text-white"
                      data-testid="input-parcelas-ja-pagas"
                    />
                    <p className="text-xs text-zinc-500 mt-1">
                      {formData.parcelas_ja_pagas && parseInt(formData.parcelas_ja_pagas) > 0 
                        ? `Parcelas 1 a ${formData.parcelas_ja_pagas} serão marcadas como pagas`
                        : 'Informe quantas parcelas você já pagou'}
                    </p>
                  </div>
                  
                  <div>
                    <Label className="text-zinc-300">Valor Total</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.valor_total}
                      onChange={(e) => setFormData({...formData, valor_total: e.target.value})}
                      placeholder="Auto-calculado se vazio"
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-zinc-300">Taxa de Juros (%/mês)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.taxa_juros_mensal}
                      onChange={(e) => setFormData({...formData, taxa_juros_mensal: e.target.value})}
                      placeholder="0,00"
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-zinc-300">Data Início *</Label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <select
                        value={formData.data_inicio_mes}
                        onChange={(e) => setFormData({...formData, data_inicio_mes: e.target.value})}
                        className="h-10 px-3 rounded-md bg-zinc-800 border border-zinc-700 text-white text-sm"
                        data-testid="input-data-inicio-mes"
                      >
                        <option value="janeiro">Janeiro</option>
                        <option value="fevereiro">Fevereiro</option>
                        <option value="março">Março</option>
                        <option value="abril">Abril</option>
                        <option value="maio">Maio</option>
                        <option value="junho">Junho</option>
                        <option value="julho">Julho</option>
                        <option value="agosto">Agosto</option>
                        <option value="setembro">Setembro</option>
                        <option value="outubro">Outubro</option>
                        <option value="novembro">Novembro</option>
                        <option value="dezembro">Dezembro</option>
                      </select>
                      <select
                        value={formData.data_inicio_ano}
                        onChange={(e) => setFormData({...formData, data_inicio_ano: e.target.value})}
                        className="h-10 px-3 rounded-md bg-zinc-800 border border-zinc-700 text-white text-sm"
                        data-testid="input-data-inicio-ano"
                      >
                        {anosDisponiveis.map(ano => (
                          <option key={ano} value={ano}>{ano}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-zinc-300">Data Fim</Label>
                    <Input
                      value={formData.data_fim || calcularDataFim(`${formData.data_inicio_mes}/${formData.data_inicio_ano}`, parseInt(formData.total_parcelas) || 0)}
                      onChange={(e) => setFormData({...formData, data_fim: e.target.value})}
                      placeholder="Auto-calculado"
                      className="bg-zinc-800 border-zinc-700 text-white h-10"
                      readOnly
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <Label className="text-zinc-300">Descrição</Label>
                    <textarea
                      value={formData.descricao}
                      onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                      placeholder="Detalhes do compromisso..."
                      className="w-full h-20 px-3 py-2 rounded-md bg-zinc-800 border border-zinc-700 text-white resize-none"
                    />
                  </div>
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    className="flex-1 border-zinc-700 text-zinc-300"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1"
                    style={{ backgroundColor: PRIMARY_COLOR }}
                    data-testid="submit-compromisso-btn"
                  >
                    {editingCompromisso ? 'Salvar' : 'Cadastrar'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Nota explicativa sobre projecao */}
        <Card className="bg-blue-500/5 border-blue-500/20">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <Info className="text-blue-400 shrink-0 mt-0.5" size={18} />
              <div>
                <p className="text-sm font-medium text-white">
                  Como os compromissos afetam sua projecao
                </p>
                <ul className="text-xs mt-2 space-y-1 text-zinc-400">
                  <li><strong className="text-blue-400">Parcelas futuras</strong> sao usadas na <strong>projecao de caixa</strong> para prever saidas.</li>
                  <li><strong className="text-orange-400">Parcelas pagas</strong> sao refletidas pelo <strong>extrato/movimento bancario real</strong> na tela de Caixa.</li>
                  <li>Isso evita duplicidade: a projecao usa o que vai vencer, o historico usa o que ja saiu do banco.</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Dashboard Cards */}
        {dashboard && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Compromissos Futuros */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: `${DANGER_COLOR}20` }}>
                    <TrendingDown className="w-5 h-5" style={{ color: DANGER_COLOR }} />
                  </div>
                  <span className="text-zinc-400 text-sm">Compromissos Futuros</span>
                </div>
                <p className="text-2xl font-bold font-numbers" style={{ color: DANGER_COLOR }}>
                  {formatCurrency(dashboard.compromisso_futuro_total)}
                </p>
                <p className="text-xs text-zinc-500 mt-1">
                  {dashboard.resumo_parcelas?.pendentes || 0} parcelas pendentes
                </p>
              </CardContent>
            </Card>
            
            {/* Total Pago */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: `${PRIMARY_COLOR}20` }}>
                    <CheckCircle2 className="w-5 h-5" style={{ color: PRIMARY_COLOR }} />
                  </div>
                  <span className="text-zinc-400 text-sm">Total Pago</span>
                </div>
                <p className="text-2xl font-bold font-numbers" style={{ color: PRIMARY_COLOR }}>
                  {formatCurrency(dashboard.total_pago)}
                </p>
                <p className="text-xs text-zinc-500 mt-1">
                  {dashboard.resumo_parcelas?.pagas || 0} parcelas pagas
                </p>
              </CardContent>
            </Card>
            
            {/* Parcelas do Mês */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: `${SECONDARY_COLOR}20` }}>
                    <Calendar className="w-5 h-5" style={{ color: SECONDARY_COLOR }} />
                  </div>
                  <span className="text-zinc-400 text-sm">Parcelas de {dashboard.mes_atual}</span>
                </div>
                <p className="text-2xl font-bold font-numbers" style={{ color: SECONDARY_COLOR }}>
                  {formatCurrency(dashboard.parcelas_mes?.valor_total || 0)}
                </p>
                <p className="text-xs text-zinc-500 mt-1">
                  {dashboard.parcelas_mes?.pagas || 0}/{dashboard.parcelas_mes?.total || 0} pagas
                </p>
              </CardContent>
            </Card>
            
            {/* Resumo */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-lg bg-zinc-800">
                    <Wallet className="w-5 h-5 text-zinc-400" />
                  </div>
                  <span className="text-zinc-400 text-sm">Compromissos Ativos</span>
                </div>
                <p className="text-2xl font-bold text-white font-numbers">
                  {dashboard.total_compromissos}
                </p>
                <p className="text-xs text-zinc-500 mt-1">
                  {dashboard.total_investimentos} invest. · {dashboard.total_financiamentos} financ.
                </p>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Lista de Compromissos */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5" style={{ color: SECONDARY_COLOR }} />
              Meus Compromissos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {compromissos.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">Nenhum compromisso cadastrado</p>
                <p className="text-zinc-500 text-sm">Clique em Novo Compromisso para adicionar</p>
              </div>
            ) : (
              <div className="space-y-4">
                {compromissos.map((comp) => (
                  <div key={comp.id} className="border border-zinc-800 rounded-lg overflow-hidden">
                    {/* Header do Compromisso */}
                    <div 
                      className="p-4 bg-zinc-800/50 cursor-pointer hover:bg-zinc-800 transition-colors"
                      onClick={() => toggleExpand(comp.id)}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`p-2 rounded-lg shrink-0 ${comp.tipo === 'investimento' ? 'bg-blue-500/20' : 'bg-orange-500/20'}`}>
                            {comp.tipo === 'investimento' ? (
                              <Target className="w-5 h-5 text-blue-400" />
                            ) : (
                              <CreditCard className="w-5 h-5 text-orange-400" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-medium text-white truncate">{comp.nome}</h3>
                            <p className="text-sm text-zinc-400">
                              {comp.tipo === 'investimento' ? 'Investimento' : 'Financiamento'}
                              {comp.categoria && ` · ${comp.categoria}`}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-4 lg:gap-6">
                          <div className="text-left lg:text-right">
                            <p className="text-xs text-zinc-400">Valor Total</p>
                            <p className="font-bold text-white font-numbers text-sm lg:text-base" data-testid={`compromisso-valor-total-${comp.id}`}>
                              {formatCurrency(comp.valor_total)}
                            </p>
                          </div>
                          <div className="text-left lg:text-right">
                            <p className="text-xs text-zinc-400">Parcela</p>
                            <p className="font-bold text-white font-numbers text-sm lg:text-base">{formatCurrency(comp.valor_parcela)}</p>
                          </div>
                          <div className="text-left lg:text-right">
                            <p className="text-xs text-zinc-400">Restante</p>
                            <p className="font-bold font-numbers text-sm lg:text-base" style={{ color: DANGER_COLOR }}>
                              {formatCurrency(comp.compromisso_restante)}
                            </p>
                          </div>
                          <div className="text-left lg:text-right">
                            <p className="text-xs text-zinc-400">Parcelas</p>
                            <p className="font-medium text-white text-sm">
                              {comp.parcelas_pagas}/{comp.total_parcelas}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 ml-auto lg:ml-0">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); handleEdit(comp); }}
                              className="text-zinc-400 hover:text-white p-2"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); handleDelete(comp.id); }}
                              className="text-zinc-400 hover:text-red-400 p-2"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            {expandedCompromisso === comp.id ? (
                              <ChevronUp className="w-5 h-5 text-zinc-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-zinc-400" />
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="mt-3">
                        <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full transition-all"
                            style={{ 
                              width: `${(comp.parcelas_pagas / comp.total_parcelas) * 100}%`,
                              backgroundColor: PRIMARY_COLOR
                            }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-zinc-500 mt-1">
                          <span>{comp.data_inicio}</span>
                          <span>{formatPercent((comp.parcelas_pagas / comp.total_parcelas) * 100, 0)} concluído</span>
                          <span>{comp.data_fim}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Parcelas Expandidas */}
                    {expandedCompromisso === comp.id && (
                      <div className="p-4 border-t border-zinc-800 bg-zinc-900/50">
                        <h4 className="text-sm font-medium text-zinc-300 mb-3">Parcelas</h4>
                        <div className="grid gap-2 max-h-96 overflow-y-auto">
                          {parcelasExpanded.map((parcela) => (
                            <div 
                              key={parcela.id}
                              className={`flex items-center justify-between p-3 rounded-lg ${
                                parcela.status === 'paga' ? 'bg-green-500/10 border border-green-500/20' : 'bg-zinc-800'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                {parcela.status === 'paga' ? (
                                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                                ) : (
                                  <Clock className="w-5 h-5 text-zinc-400" />
                                )}
                                <div>
                                  <p className="text-white font-medium">
                                    Parcela {parcela.numero_parcela}/{comp.total_parcelas}
                                  </p>
                                  <p className="text-xs text-zinc-400">{parcela.mes_referencia}</p>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-4">
                                <span className="font-bold font-numbers text-white">
                                  {formatCurrency(parcela.valor)}
                                </span>
                                
                                {parcela.status === 'paga' ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDesfazerPagamento(parcela.id)}
                                    className="text-xs border-zinc-700 text-zinc-400 hover:text-white"
                                  >
                                    Desfazer
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    onClick={() => handleMarcarPaga(parcela.id)}
                                    className="text-xs"
                                    style={{ backgroundColor: PRIMARY_COLOR }}
                                  >
                                    Marcar Paga
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default CompromissosPage;
